import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@nextui-org/react";
import clsx from "clsx";
import { ChevronDown, type LucideIcon } from "lucide-react";
import React from "react";

type DropdownOption = {
  key: string;
  label: string;
  onClick: () => void;
  icon?: LucideIcon | React.FC<React.SVGProps<SVGSVGElement>>;
  showDividerAfter?: boolean;
  showDividerBefore?: boolean;
};

type DropdownButtonProps = {
  label: string;
  options: DropdownOption[];
  isDisabled?: boolean;
  variant?: "primary" | "bordered";
  className?: string;
  placement?:
    | "bottom-end"
    | "bottom-start"
    | "bottom"
    | "top"
    | "top-start"
    | "top-end"
    | "left"
    | "left-start"
    | "left-end"
    | "right"
    | "right-start"
    | "right-end";
};

export const ZDropdownButton: React.FC<DropdownButtonProps> = ({
  label,
  options,
  isDisabled = false,
  variant = "primary",
  className,
  placement = "bottom-end",
}) => {
  const buttonClassName =
    variant === "primary"
      ? "bg-primary text-white px-3 py-1 h-auto rounded-md border-r border-white/30 [&:disabled]:opacity-100 [&:disabled]:bg-primary [&:disabled]:text-white"
      : "border border-primary bg-white text-primary px-3 py-1 h-7 min-h-7 rounded-none hover:bg-primary/5 font-medium text-sm";

  return (
    <Dropdown
      placement={placement}
      isDisabled={isDisabled}
      classNames={{
        content: "rounded-md",
      }}
    >
      <DropdownTrigger>
        <Button className={clsx(buttonClassName, className)} isDisabled={isDisabled}>
          {label}
          {variant === "bordered" && <div className="h-6 w-px bg-primary mx-1" />}
          <ChevronDown className="size-4" />
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={`${label} options`}
        onAction={(key) => {
          const option = options.find((opt) => opt.key === key);
          if (option) {
            option.onClick();
          }
        }}
      >
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <DropdownItem
              key={option.key}
              className={clsx({
                "border-b border-gray-200": option.showDividerAfter,
                "border-t border-gray-200": option.showDividerBefore,
              })}
            >
              {Icon ? (
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span>{option.label}</span>
                </div>
              ) : (
                option.label
              )}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};
